import { mergeStyles, ProgressIndicator } from '@fluentui/react';
import * as React from 'react';
import { captureContentUpdate } from '../../../../../web/utils/oneDsUtil';
import { getString } from '../../../../../web/utils/stringLoader';
import { AutomationIds } from '../../../../models/constants/automationIds';
import {
  IUITabViewInfo,
} from '../../../../models/reduxState';
import { RequestCardContainer } from '../../containerComponents/RequestHistory/RequestCardContainer';
import { FreelanceRequest } from '../../models/requestModels';
import {
  getCommonClassNames,
  getCommonStyles,
  getCommonThemedClassNames,
  getCommonThemedStyles,
} from '../../styles/commonStyles';
import {
  getRequestListClassNames,
  IRequestListStyles,
} from '../../styles/requestHistoryStyles';

import { IStyledComponent } from '../../styles/themesUI';

type IRequestListComponentProps = {
  isSearchingRequests: boolean;
  allRequests: FreelanceRequest[];
  scrollToId?: string;
  // dispatchers
  updateUITabViewInfo: (uiTabViewInfo: IUITabViewInfo) => void;
  dispatchGetRequest: (requestId: string) => void;
};

type IRequestListProps = IRequestListComponentProps & IStyledComponent<IRequestListStyles>;

const commonClassNames = getCommonClassNames(getCommonStyles);

export class RequestList extends React.PureComponent<IRequestListProps> {
  private containerRef = React.createRef<HTMLDivElement>();
  private commonThemedClassnames = getCommonThemedClassNames(
    getCommonThemedStyles,
    { theme: this.props.theme }
  );
  private requestListClassNames = getRequestListClassNames(
    this.props.styles,
    { theme: this.props.theme }
  );

  public componentDidMount(): void {
    this._logRequestListShown('componentDidMount');
    if (this.props.scrollToId) {
      this.scrollToRequest(this.props.scrollToId);
    }
  }

  public componentDidUpdate(prevProps: IRequestListProps): void {
    if (
      this.props.allRequests &&
      (!prevProps.allRequests ||
        (prevProps.allRequests.length === 0 &&
          this.props.allRequests.length > 0))
    ) {
      this._logRequestListShown('componentDidUpdate');
    }

    if (
      prevProps.scrollToId !== this.props.scrollToId &&
      this.props.scrollToId
    ) {
      this.scrollToRequest(this.props.scrollToId);
    }
  }

  public render(): React.ReactNode {
    if (this.props.isSearchingRequests) {
      return this.renderProgressIndicator();
    }

    const requestsContent =
      this.props.allRequests && this.props.allRequests.length > 0
        ? this.renderRequestList()
        : this.renderNoRequestMessage();

    return (
      <>
        <div
          className={mergeStyles(
            commonClassNames.marginTop10,
            this.commonThemedClassnames.SeparatorLineStyle
          )}
        />
        <div ref={this.containerRef}>{requestsContent}</div>
      </>
    );
  }

  private _logRequestListShown = (methodName: string) => {
    const logHandlerTags = {
      content: {
        contentId: 'RequestListShown',
        contentName: 'RequestListShown',
        formnm: 'maxFunnel',
        fileName: 'RequestList.tsx',
        methodName: methodName,
        areaName: 'RequestHistory',
        actionType: 'KE',
        requestsLength: this.props.allRequests
          ? this.props.allRequests.length
          : 0,
      },
      behavior: 'SHOW',
    };
    captureContentUpdate(logHandlerTags);
  };

  private renderProgressIndicator = (): React.ReactNode => {
    return (
      <ProgressIndicator
        description={
          getString('requestHistory_searchRequestsLoadingMessage')
        }
        styles={
          this.requestListClassNames.subComponentStyles.progressIndicatorStyles
        }
      />
    );
  };

  private renderRequestList = (): React.ReactNode => {
    if (!!this.props.allRequests && this.props.allRequests.length > 0) {
      return this.props.allRequests.map(
        (request: FreelanceRequest, index: number) => {
          if (!!request) {
            const requestId = request.RequestId;
            return (
              <div key={requestId} data-key={requestId}>
                <RequestCardContainer
                  data-automation-id={AutomationIds.RequestCard + '-' + index}
                  request={request}
                  updateUITabViewInfo={this.props.updateUITabViewInfo}
                  dispatchGetRequest={this.props.dispatchGetRequest}
                  theme={this.props.theme}
                />
                {index !== this.props.allRequests.length - 1 && (
                  <div
                    key={`requestcard_separator_${requestId}`}
                    className={mergeStyles(
                      commonClassNames.marginLeft40,
                      this.commonThemedClassnames.SeparatorLineStyle
                    )}
                  />
                )}
              </div>
            );
          } else {
            return null;
          }
        }
      );
    } else {
      return null;
    }
  };

  private renderNoRequestMessage = (): React.ReactNode => {
    return (
      <div className={this.requestListClassNames.noRequestMessage}>
        {getString('requestHistory_searchRequestsNoRequestMessage')}
      </div>
    );
  };

  private scrollToRequest = (id: string) => {
    if (this.containerRef && this.containerRef.current) {
      const request = this.containerRef.current!.querySelector(
        `div[data-key="${id}"]`
      ) as HTMLElement;
      if (request) {
        request.scrollIntoView();
      }
    }
  };
}
